import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Search, Stethoscope, Scissors, BedDouble, Sparkles, X, Check } from "lucide-react";
import api from "@/lib/api";
import { IMG, DEPT, Title, Eyebrow, Nav, PetAvatar } from "./ui";

const ICON = { vet: Stethoscope, grooming: Scissors, boarding: BedDouble, other: Sparkles };

export function Summary({ name, pets, items, onRemove, compact }) {
  return (
    <aside className="sb-card p-6 lg:sticky lg:top-28" data-testid="sb-summary">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--sb-lav)]">Your visit</p>
      <p className="sb-serif mt-2 break-words text-3xl font-semibold">{name || "Welcome"}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        {pets.map((p) => <span key={p.id} className="flex min-w-0 max-w-full items-center gap-2 break-words rounded-full bg-[var(--sb-lav-bg)] py-1 pl-1 pr-4 text-sm font-semibold"><PetAvatar pet={p} size="h-8 w-8 rounded-full text-base" />{p.name}</span>)}
      </div>
      {!compact && (
        <div className="mt-6 border-t border-[var(--sb-line)] pt-5">
          <p className="text-sm font-semibold text-[var(--sb-muted)]">{items.length ? `${items.length} service${items.length > 1 ? "s" : ""} chosen` : "No services yet"}</p>
          <ul className="mt-3 space-y-2" data-testid="sb-summary-items">
            {items.map((i) => (
              <li key={i.service_id} className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--sb-ivory)] px-4 py-3 text-sm">
                <span className="min-w-0"><span className="block break-words font-semibold">{i.name}</span><span className="text-xs text-[var(--sb-muted)]">{DEPT[i.dept].label}</span></span>
                {onRemove && <button type="button" aria-label={`Remove ${i.name}`} onClick={() => onRemove(i)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full hover:bg-white" data-testid={`sb-remove-${i.service_id}`}><X className="h-4 w-4" /></button>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

function CategoryCard({ k, count, onClick, chosen }) {
  const Icon = ICON[k];
  return (
    <button type="button" onClick={onClick} data-testid={`sb-cat-${k}`}
      className="sb-lift group relative overflow-hidden rounded-[2rem] text-left shadow-[0_10px_30px_-14px_rgba(74,21,75,0.25)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(156,130,184,.4)]">
      <img src={IMG[k]} alt="" className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-64" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2A182B]/85 via-[#2A182B]/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 text-white">
        <span className="mb-3 inline-grid h-11 w-11 place-items-center rounded-full bg-white/20 backdrop-blur-md"><Icon className="h-5 w-5" /></span>
        <p className="sb-serif text-3xl font-semibold">{DEPT[k].label}</p>
        <p className="mt-1 text-sm text-white/85">{DEPT[k].blurb}</p>
      </div>
      {chosen > 0 && <span className="absolute right-4 top-4 rounded-full bg-white px-3 py-1 text-sm font-semibold text-[var(--sb-plum)]">{chosen} chosen</span>}
      <span className="sr-only">{count} services</span>
    </button>
  );
}

function ServiceCard({ s, selected, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={selected} data-testid={`sb-svc-${s.id}`}
      className={`sb-lift flex min-h-[96px] items-start justify-between gap-3 rounded-3xl border-2 p-5 text-left ${selected ? "border-[var(--sb-plum)] bg-[var(--sb-lav-bg)]" : "border-transparent bg-white shadow-[0_8px_24px_-14px_rgba(74,21,75,0.25)]"}`}>
      <span className="min-w-0">
        <span className="block break-words text-lg font-semibold leading-snug">{s.name}</span>
        {s.description && <span className="mt-1 line-clamp-2 block text-sm text-[var(--sb-muted)]">{s.description}</span>}
      </span>
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors ${selected ? "bg-[var(--sb-plum)] text-white" : "bg-[var(--sb-lav-bg)] text-[var(--sb-plum)]"}`}><Check className={`h-4 w-4 ${selected ? "" : "opacity-30"}`} /></span>
    </button>
  );
}

export function Services({ name, pets, items, onChange, onNext, onBack }) {
  const reduce = useReducedMotion();
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const cata = useQuery({ queryKey: ["sb-catalog"], queryFn: async () => (await api.get("/self-booking/catalog")).data, staleTime: 300e3 });
  const search = useQuery({ queryKey: ["sb-catalog-q", q], enabled: q.trim().length >= 2,
    queryFn: async () => (await api.get("/self-booking/catalog", { params: { q } })).data.results });
  const has = (id) => items.some((i) => i.service_id === id);
  const toggle = (s) => onChange(has(s.id) ? items.filter((i) => i.service_id !== s.id) : [...items, { service_id: s.id, name: s.name, dept: s.dept }]);
  const cats = cata.data?.categories || [];
  const active = cats.find((c) => c.key === cat);
  const list = q.trim().length >= 2 ? (search.data || []).filter((s) => s.dept === cat) : active?.featured || [];
  return (
    <div data-testid="sb-step-services">
      <Eyebrow>Services</Eyebrow>
      <div className="mt-3"><Title sub="Choose what your pet needs. We'll take care of the rest.">What are you looking for today?</Title></div>
      <div className="mt-10 grid gap-8 lg:grid-cols-[320px_1fr]">
        <div className="order-2 lg:order-1"><Summary name={name} pets={pets} items={items} onRemove={(i) => onChange(items.filter((x) => x.service_id !== i.service_id))} /></div>
        <div className="order-1 min-w-0 lg:order-2">
          {cata.isLoading && <div className="grid gap-5 sm:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-56 animate-pulse rounded-[2rem] bg-[var(--sb-cream)]" />)}</div>}
          {cata.isError && <p className="sb-card p-6 text-[#B4232A]" role="alert" data-testid="sb-catalog-error">We couldn&rsquo;t load services just now. Please ask reception for help.</p>}
          <AnimatePresence mode="wait">
            {!cat ? (
              <motion.div key="cats" initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid gap-5 sm:grid-cols-2">
                {cats.map((c) => <CategoryCard key={c.key} k={c.key} count={c.count} chosen={items.filter((i) => i.dept === c.key).length} onClick={() => { setCat(c.key); setQ(""); }} />)}
                {cata.isSuccess && !cats.length && <p className="text-[var(--sb-muted)]" data-testid="sb-no-services">No services are set up yet. Please ask reception for help.</p>}
              </motion.div>
            ) : (
              <motion.div key={cat} initial={reduce ? false : { opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.3 }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button type="button" onClick={() => setCat("")} className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-white px-5 font-semibold text-[var(--sb-plum)] ring-1 ring-[var(--sb-lav-line)]" data-testid="sb-cat-back"><ArrowLeft className="h-4 w-4" /> All categories</button>
                  <p className="sb-serif text-3xl font-semibold" data-testid="sb-cat-title">{DEPT[cat].label}</p>
                </div>
                <div className="relative mt-6">
                  <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--sb-lav)]" />
                  <input type="search" enterKeyHint="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${DEPT[cat].label.toLowerCase()} services`} className="sb-input sb-input--icon" data-testid="sb-svc-search" />
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {list.map((s) => <ServiceCard key={s.id} s={s} selected={has(s.id)} onToggle={() => toggle(s)} />)}
                  {q.trim().length >= 2 && search.isSuccess && !list.length && <p className="text-[var(--sb-muted)]">No matching services.</p>}
                </div>
                {!q && active && active.count > active.featured.length && <p className="mt-4 text-sm text-[var(--sb-muted)]">Showing our most-loved {DEPT[cat].label.toLowerCase()} services. Search to see all {active.count}.</p>}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <Nav onBack={onBack} onNext={onNext} disabled={!items.length} />
    </div>
  );
}
